class FmProductImages extends HTMLElement {
  connectedCallback() {
    this._onSectionSelect = this._handleSectionSelect.bind(this);
    document.addEventListener('shopify:section:select', this._onSectionSelect);

    this._carouselBindings = [];

    this.querySelectorAll('.fm-product-images__carousel-wrapper').forEach((wrapper) => {
      const carousel = wrapper.querySelector('.fm-product-images__carousel');

      if (!carousel || typeof FoxTheme === 'undefined' || !FoxTheme.Carousel) {
        return;
      }

      const prevButton = wrapper.querySelector('[data-action="prev"]');
      const nextButton = wrapper.querySelector('[data-action="next"]');
      const loop = carousel.dataset.loop === 'true';
      const autoplayRaw = carousel.dataset.autoplay;
      const autoplayEnabled = Boolean(autoplayRaw) && autoplayRaw !== 'false';
      const spaceBetween = parseFloat(getComputedStyle(this).getPropertyValue('--fm-image-gap')) || 0;

      const options = {
        slidesPerView: 1,
        spaceBetween,
        autoHeight: true,
        loop,
        threshold: 2,
        watchOverflow: true,
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
      };

      let modules = null;

      if (autoplayEnabled) {
        options.autoplay = {
          delay: parseInt(autoplayRaw, 10) || 5000,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        };
        modules = FoxTheme.Swiper.Autoplay;
      }

      const sliderInstance = new FoxTheme.Carousel(carousel, options, modules);
      sliderInstance.init();

      const binding = {
        carousel,
        sliderInstance,
        prevButton,
        nextButton,
        loop,
        onCarouselClick: (event) => this._handleCarouselClick(event, binding),
        onCarouselMouseMove: (event) => this._handleCarouselMouseMove(event, carousel),
        onCarouselMouseLeave: () => this._handleCarouselMouseLeave(carousel),
        onSlideChange: () => this._updateNavButtons(binding),
      };

      carousel.addEventListener('click', binding.onCarouselClick);
      carousel.addEventListener('mousemove', binding.onCarouselMouseMove);
      carousel.addEventListener('mouseleave', binding.onCarouselMouseLeave);

      if (sliderInstance.slider) {
        sliderInstance.slider.on('slideChange', binding.onSlideChange);
        sliderInstance.slider.on('init', binding.onSlideChange);
      }

      carousel.classList.remove('is-pending-ready');
      this._updateNavButtons(binding);
      this._carouselBindings.push(binding);
    });
  }

  disconnectedCallback() {
    if (this._onSectionSelect) {
      document.removeEventListener('shopify:section:select', this._onSectionSelect);
    }

    this._carouselBindings.forEach((binding) => {
      binding.carousel.removeEventListener('click', binding.onCarouselClick);
      binding.carousel.removeEventListener('mousemove', binding.onCarouselMouseMove);
      binding.carousel.removeEventListener('mouseleave', binding.onCarouselMouseLeave);

      if (binding.sliderInstance?.slider) {
        binding.sliderInstance.slider.off('slideChange', binding.onSlideChange);
        binding.sliderInstance.slider.destroy(true, true);
      }
    });

    this._carouselBindings = [];
  }

  _handleSectionSelect(event) {
    const sectionElement = event.target;

    if (!sectionElement || !sectionElement.contains(this)) {
      return;
    }

    this._carouselBindings.forEach((binding) => {
      const slider = binding.sliderInstance?.slider;

      if (!slider) {
        return;
      }

      slider.update();

      if (typeof slider.updateAutoHeight === 'function') {
        slider.updateAutoHeight(0);
      }
    });
  }

  _handleCarouselClick(event, binding) {
    if (window.matchMedia('(max-width: 767.98px)').matches || !binding?.sliderInstance?.slider) {
      return;
    }

    if (event.target.closest('button, a')) {
      return;
    }

    const rect = binding.carousel.getBoundingClientRect();
    const center = rect.left + rect.width / 2;

    if (event.clientX > center) {
      binding.sliderInstance.slider.slideNext();
    } else {
      binding.sliderInstance.slider.slidePrev();
    }
  }

  _handleCarouselMouseMove(event, carousel) {
    if (window.matchMedia('(max-width: 767.98px)').matches || !carousel) {
      return;
    }

    const rect = carousel.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const isHoverRight = event.clientX > center;

    carousel.classList.toggle('is-hovering-right', isHoverRight);
    carousel.classList.toggle('is-hovering-left', !isHoverRight);
  }

  _handleCarouselMouseLeave(carousel) {
    if (!carousel) {
      return;
    }

    carousel.classList.remove('is-hovering-right', 'is-hovering-left');
  }

  _updateNavButtons(binding) {
    if (!binding?.prevButton || !binding?.nextButton || !binding.sliderInstance?.slider) {
      return;
    }

    if (binding.loop) {
      binding.prevButton.disabled = false;
      binding.nextButton.disabled = false;
      return;
    }

    const slider = binding.sliderInstance.slider;
    const lastIndex = slider.slides.length - 1;

    binding.prevButton.disabled = slider.isBeginning || slider.activeIndex === 0;
    binding.nextButton.disabled = slider.isEnd || slider.activeIndex === lastIndex;
  }
}

if (!customElements.get('fm-product-images')) {
  customElements.define('fm-product-images', FmProductImages);
}
